import { useState, type FormEvent } from 'react'
import { Link, Navigate, useLocation } from 'react-router-dom'
import { AlertCircle, Eye, EyeOff, LoaderCircle } from 'lucide-react'
import PageIntro from '../components/ui/PageIntro'
import Section from '../components/ui/Section'
import Button from '../components/ui/Button'
import { AccountField, AccountPanel, FIELD_CLASS } from '../components/account/AccountUi'
import { useAccount } from '../hooks/account-context'
import { StaffAccountError } from '../lib/session'
import { toApiError } from '../lib/http'

/* The buyer's door.

   Not the console's. Somebody who works here signs in on the other app, and
   their password is perfectly good — so a staff account typed in this box is
   answered by saying which door it opens rather than by "wrong password",
   which would send them round a reset loop for an account that is fine.

   `from` is remembered so a link straight to an instalment lands on that
   instalment after the password, rather than on the account's front page. */

export function SignIn() {
  const { status, signIn } = useAccount()
  const location = useLocation()

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const from = (location.state as { from?: string } | null)?.from ?? '/account'

  if (status === 'signed-in') return <Navigate to={from} replace />

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    if (busy) return

    setBusy(true)
    setError('')

    try {
      await signIn(username, password)
      /* Nothing to navigate: the session change re-renders this component
         and the redirect above takes it from there. */
    } catch (failure) {
      setError(
        failure instanceof StaffAccountError
          ? 'That is a showroom sign-in. Staff accounts open the console, not this page.'
          : toApiError(failure).message,
      )
      setPassword('')
      setBusy(false)
    }
  }

  return (
    <>
      <PageIntro
        eyebrow="Your account"
        title="Sign in"
        ghost="Account"
        lede="Your bookings, what you have paid against them, the months on your bank plan, and the cars in your name."
      />

      <Section className="!py-16 sm:!py-20">
        <div className="mx-auto w-full max-w-[460px]">
          <AccountPanel>
            <form onSubmit={onSubmit} noValidate className="flex flex-col gap-5">
              {error ? (
                <p
                  role="alert"
                  className="flex items-start gap-2.5 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-[13px] leading-relaxed text-red-200"
                >
                  <AlertCircle aria-hidden="true" className="mt-px size-4 shrink-0" strokeWidth={2} />
                  {error}
                </p>
              ) : null}

              <AccountField label="Username" htmlFor="signin-username">
                <input
                  id="signin-username"
                  value={username}
                  autoFocus
                  autoComplete="username"
                  onChange={(event) => setUsername(event.target.value)}
                  className={FIELD_CLASS}
                />
              </AccountField>

              <AccountField label="Password" htmlFor="signin-password">
                <span className="relative block">
                  <input
                    id="signin-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    autoComplete="current-password"
                    onChange={(event) => setPassword(event.target.value)}
                    className={`${FIELD_CLASS} pr-12`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((value) => !value)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute inset-y-0 right-0 grid w-12 place-items-center text-dim transition-colors hover:text-lime"
                  >
                    {showPassword ? (
                      <EyeOff aria-hidden="true" className="size-4" strokeWidth={1.9} />
                    ) : (
                      <Eye aria-hidden="true" className="size-4" strokeWidth={1.9} />
                    )}
                  </button>
                </span>
              </AccountField>

              <Button type="submit" size="lg" disabled={busy} className="w-full">
                {busy ? (
                  <>
                    <LoaderCircle aria-hidden="true" className="size-4 animate-spin" strokeWidth={2} />
                    Signing in
                  </>
                ) : (
                  'Sign in'
                )}
              </Button>
            </form>
          </AccountPanel>

          <p className="mt-6 text-center text-[13.5px] text-steel">
            First time here?{' '}
            <Link to="/register" className="font-medium text-lime hover:text-lime-lift">
              Create an account
            </Link>
            . If you have booked with us before, use the same mobile number and we will find your
            file.
          </p>
        </div>
      </Section>
    </>
  )
}

export default SignIn
